import { useState, useCallback, useMemo } from "react";
import { useUserContext } from "./contexts/useUserContext";
/**
 * Hook for checking JuristIQ access permissions
 * Based on the access control patterns from JuristicWebApp
 */ export const useJuristIQAccess = ()=>{
    const { user } = useUserContext((s)=>s);
    const [isLoading, setIsLoading] = useState(false);
    const [denialReason, setDenialReason] = useState(null);
    const checkJuristIQAccess = useCallback((userToCheck)=>{
        const targetUser = userToCheck || user;
        if (!targetUser) {
            setDenialReason("User not authenticated");
            return false;
        }
        const dbUser = targetUser.meta?.dbUser;
        const organization = targetUser.meta?.organization;
        // Check if user exists and has meta data
        if (!dbUser || !organization) {
            setDenialReason("User information incomplete");
            return false;
        }
        // Check if user account is active
        if (dbUser.status !== "active") {
            setDenialReason("Your account is not active. Please contact your administrator.");
            return false;
        }
        // Check if organization has an active plan
        if (!organization.plan_id) {
            setDenialReason("Your organization doesn't have an active plan. Please contact your administrator to upgrade.");
            return false;
        }
        // Check if user has JuristIQ access
        // Based on JuristicWebApp pattern: accesses?.includes("juristiq")
        const userAccesses = dbUser.accesses || [];
        const hasJuristIQAccess = userAccesses.includes("juristiq");
        if (!hasJuristIQAccess) {
            setDenialReason("You don't have access to JuristIQ. Please contact your administrator to request JuristIQ access.");
            return false;
        }
        // All checks passed
        setDenialReason(null);
        return true;
    }, [
        user
    ]);
    const hasAccess = useMemo(()=>{
        if (!user) {
            return false;
        }
        setIsLoading(true);
        const isAccessGranted = checkJuristIQAccess();
        setIsLoading(false);
        return isAccessGranted;
    }, [
        checkJuristIQAccess,
        user
    ]);
    const refreshAccess = useCallback(()=>{
        setIsLoading(true);
        checkJuristIQAccess();
        setIsLoading(false);
    }, [
        checkJuristIQAccess
    ]);
    return {
        hasAccess,
        isLoading,
        denialReason,
        checkAccess: checkJuristIQAccess,
        refreshAccess
    };
};
/**
 * Enhanced hook that provides detailed access information
 */ export const useJuristIQAccessDetails = ()=>{
    const { user } = useUserContext((s)=>s);
    const [isLoading, setIsLoading] = useState(false);
    const accessDetails = useMemo(()=>{
        setIsLoading(true);
        const result = {
            hasJuristIQAccess: false,
            hasActivePlan: false,
            isActiveUser: false,
            hasValidOrganization: false
        };
        if (!user?.meta?.dbUser || !user?.meta?.organization) {
            setIsLoading(false);
            return result;
        }
        const dbUser = user.meta.dbUser;
        const organization = user.meta.organization;
        // Check individual access components
        result.isActiveUser = dbUser.status === "active";
        result.hasValidOrganization = !!organization.plan_id;
        result.hasActivePlan = !!organization.plan_id;
        result.hasJuristIQAccess = (dbUser.accesses || []).includes("juristiq");
        setIsLoading(false);
        return result;
    }, [
        user
    ]);
    const hasAccess = useMemo(()=>{
        return accessDetails.hasJuristIQAccess && accessDetails.hasActivePlan && accessDetails.isActiveUser && accessDetails.hasValidOrganization;
    }, [
        accessDetails
    ]);
    const denialReason = useMemo(()=>{
        if (!user?.meta?.dbUser || !user?.meta?.organization) {
            return "User information incomplete";
        }
        if (!accessDetails.isActiveUser) {
            return "Your account is not active. Please contact your administrator.";
        }
        if (!accessDetails.hasActivePlan) {
            return "Your organization doesn't have an active plan. Please contact your administrator to upgrade.";
        }
        if (!accessDetails.hasJuristIQAccess) {
            return "You don't have access to JuristIQ. Please contact your administrator to request JuristIQ access.";
        }
        return null;
    }, [
        accessDetails,
        user
    ]);
    return {
        hasAccess,
        isLoading,
        denialReason,
        accessDetails
    };
};
